import { BaseRestRepository } from './baseRestRepository';
import type { RequestUploadSessionDto, UploadSessionDto } from '~/swagger/portal/Api';

export type UploadFileOptions = {
  abortSignal?: AbortSignal;
  showErrorMessage?: boolean;
  maxConcurrency?: number;
  onFileComplete?: (file: File, sessionId: string) => void;
};

export class UploadSessionRepository extends BaseRestRepository {
  private async requestUploadSession(url: string, file: File, signal?: AbortSignal, showErrorMessage = true): Promise<UploadSessionDto> {
    const payload: RequestUploadSessionDto = {
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type || 'application/octet-stream',
    };

    const response = await this.fetchWithAuth(
      url,
      {
        method: 'POST',
        body: JSON.stringify(payload),
        signal,
      },
      showErrorMessage
    );
    return this.getJsonResponse(response);
  }

  private async uploadToPresignedUrl(session: UploadSessionDto, file: File, signal?: AbortSignal): Promise<void> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(session.fields)) {
      formData.append(key, value);
    }
    formData.append('file', file);

    const response = await fetch(session.endpoint, {
      method: session.method,
      body: formData,
      signal,
    });

    if (!response.ok) {
      throw new Error(`Object storage upload failed: ${response.status} ${response.statusText}`);
    }
  }

  public async uploadFiles(sessionUrl: string, files: File[], opts?: UploadFileOptions): Promise<string[]> {
    if (files.length === 0) return [];

    const iterator = files[Symbol.iterator]();
    const concurrency = Math.max(1, Math.min(opts?.maxConcurrency ?? 3, files.length));
    const results = new Map<number, string>();

    const worker = async () => {
      while (true) {
        const next = iterator.next();
        if (next.done) return;
        const file = next.value;
        const index = files.indexOf(file);
        const session = await this.requestUploadSession(sessionUrl, file, opts?.abortSignal, opts?.showErrorMessage);
        await this.uploadToPresignedUrl(session, file, opts?.abortSignal);
        results.set(index, session.sessionId);
        opts?.onFileComplete?.(file, session.sessionId);
      }
    };

    await Promise.all(Array.from({ length: concurrency }, () => worker()));
    return files.map((_, i) => results.get(i)!);
  }

  public async uploadFile(sessionUrl: string, file: File, opts?: UploadFileOptions): Promise<string> {
    const session = await this.requestUploadSession(sessionUrl, file, opts?.abortSignal, opts?.showErrorMessage);
    await this.uploadToPresignedUrl(session, file, opts?.abortSignal);
    return session.sessionId;
  }
}

export const uploadSessionRepository = new UploadSessionRepository();
